<script lang="ts">
  import { browser } from '$app/environment';

  import DropdownList from '$shared/atoms/DropdownList/DropdownList.svelte';

  interface Props {
    list?: CommonList[];
    show?: boolean;
    alignRight?: boolean;
    hasOverflow?: boolean;
    children?: import('svelte').Snippet;
    listId?: string;
    onclick?: (item: CommonList) => void;
    onclose?: () => void;
    /** Consumers own `show`, so opening must route back through their own open
     *  routine — Menu's also calls setScrollListener(), which is the only thing
     *  that positions the fixed (hasOverflow) list. */
    onopen?: () => void;
    /** Reports the highlighted option's DOM id so a consumer can point its
     *  trigger's aria-activedescendant at it. */
    onhighlight?: (optionId?: string) => void;
  }

  let {
    list = [],
    show = false,
    alignRight = false,
    hasOverflow = false,
    children,
    listId,
    onclick,
    onclose,
    onopen,
    onhighlight
  }: Props = $props();

  let parent: HTMLElement = $state();
  let labelElement: HTMLElement = $state();
  let listElement: HTMLElement = $state();
  // Lives inside {#if list.length !== 0}, so it is unbound for empty lists.
  let dropdownListElement: DropdownList | undefined = $state();

  // `show` is a plain prop, so there is no setter to hook the reset onto.
  $effect(() => {
    if (!show) {
      dropdownListElement?.reset();
    }
  });

  /**
   * Keyboard handling lives on the wrapper so one listener serves both the
   * trigger and the list, which both sit inside it. Deliberately no tabindex —
   * every consumer's trigger is a real <button> that already holds focus, and a
   * tabindex here would add a spurious tab stop at each of ~29 render sites.
   */
  const onKeyDown = (e: KeyboardEvent): void => {
    if (!show) {
      // The trigger is inside this wrapper, so every keystroke on a focused
      // trigger lands here. Only the arrows may act while closed; anything else
      // would drive an invisible highlight and swallow the key.
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        onopen?.();
        // Open AND land on the first (or last) option in one keystroke, per the
        // ARIA combobox pattern. Safe to move the highlight before `show` has
        // propagated: the reset effect only fires while show is false, and it
        // has already run for this state.
        dropdownListElement?.handleKeyDown(e);
      }
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      // Mandatory: these menus render inside modals, and Modal/SidebarContainer
      // both close on a window-level Escape.
      e.stopPropagation();
      onclose?.();
      return;
    }

    if (e.key === 'Tab') {
      onclose?.();
      return;
    }

    if (dropdownListElement?.handleKeyDown(e)) {
      e.preventDefault();
    }
  };

  const onClickWindow = (e: any) => {
    if (parent && !parent.contains(e.target)) {
      onclose?.();
      removeScrollEventListner();
    }
  };

  const addScrollEventListener = (): void => {
    browser && window.addEventListener('svelte-scroll', () => setPosition());
  };

  const removeScrollEventListner = (): void => {
    browser && window.removeEventListener('svelte-scroll', () => setPosition());
  };

  const calculatePosition = (dimension: DOMRect): { x: number; y: number } => {
    let x = 0;
    let y = 0;
    if (dimension) {
      const dropdownListDim = listElement?.getBoundingClientRect();
      x = dimension.left + dimension.width - (dropdownListDim?.width || 0);
      y = dimension.top + dimension.height + 8;
    }

    return { x, y };
  };

  const setPosition = (): void => {
    const dim = labelElement?.getBoundingClientRect();
    if (!dim) return;

    const { x, y } = calculatePosition(dim);
    listElement.style.top = y + 'px';
    listElement.style.left = x + 'px';
  };

  export const setScrollListener = (): void => {
    if (!hasOverflow) return;
    addScrollEventListener();
    setPosition();
  };
</script>

<svelte:window onmouseup={onClickWindow} />

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div bind:this={parent} class="comp-with-list" class:alignRight onkeydown={onKeyDown}>
  <div bind:this={labelElement} class="label">
    {@render children?.()}
  </div>
  {#if list.length !== 0}
    <div bind:this={listElement} class="list" class:show class:fixed={hasOverflow}>
      <DropdownList
        maxheight
        {list}
        {listId}
        {onclick}
        {onhighlight}
        bind:this={dropdownListElement}
      />
    </div>
  {/if}
</div>

<style>
  .comp-with-list {
    position: relative;
  }
  .comp-with-list.alignRight .list {
    left: 0;
    right: auto;
  }
  .list {
    display: none;
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    z-index: 99;
  }
  .list.fixed {
    position: fixed;
    right: unset;
  }
  .list.show {
    display: block;
  }
</style>
